import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { PiArrowRight, PiLockSimple } from "react-icons/pi";
import api from "../chat/api";
import { toast } from "../../utils/appToast";
import {
  PageHeader,
  SettingRow,
  TextInput,
  LockedField,
  SecondaryButton,
  SaveBar,
  ViewOnlyBanner,
  initialsOf,
  useSettings,
} from "./settings/ui";

const NAME_MAX = 40;

const updateCachedWorkspaceName = (id, name) => {
  const rename = (w) => (String(w?.id) === String(id) ? { ...w, name } : w);
  try {
    const list = JSON.parse(localStorage.getItem("cachedWorkspaces") || "null");
    if (Array.isArray(list)) localStorage.setItem("cachedWorkspaces", JSON.stringify(list.map(rename)));
    const active = JSON.parse(localStorage.getItem("activeWorkspace") || "null");
    if (active) localStorage.setItem("activeWorkspace", JSON.stringify(rename(active)));
  } catch {
    // ignore malformed cache
  }
};

const GeneralSettings = () => {
  const navigate = useNavigate();
  const { orgRole, workspaceName } = useSettings();
  const isAdmin = orgRole === "Owner";
  const [org, setOrg] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const [inviteCount, setInviteCount] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    const orgId = Cookies.get("selected_org_id");
    if (!orgId) return;
    setLoadError(false);
    try {
      const res = await api.get(`/api/v1/org/details/?organization_id=${orgId}`);
      if (Cookies.get("selected_org_id") !== orgId) return;
      setOrg(res.data || null);
      setDraftName(res.data?.name || "");
    } catch (error) {
      console.error("Error fetching workspace details:", error);
      setOrg(null);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener("workspace-changed", load);
    return () => window.removeEventListener("workspace-changed", load);
  }, [load]);

  useEffect(() => {
    if (!org?.id || !isAdmin) {
      setInviteCount(null);
      return;
    }
    api
      .get(`/api/v1/org/invites/list/${org.id}/`)
      .then((res) =>
        setInviteCount(Array.isArray(res.data) ? res.data.filter((i) => i.status !== "accepted").length : null)
      )
      .catch(() => setInviteCount(null));
  }, [org?.id, isAdmin]);

  const savedName = org?.name || workspaceName;
  const trimmed = draftName.trim();
  const dirty = isAdmin && !!org && draftName !== org.name;

  const handleSave = async () => {
    if (!trimmed) {
      setNameError("Workspace name can't be empty.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch("/api/v1/org/", { organization_id: org.id, name: trimmed });
      const name = res.data?.name || trimmed;
      setOrg((o) => ({ ...o, name }));
      setDraftName(name);
      updateCachedWorkspaceName(org.id, name);
      window.dispatchEvent(new CustomEvent("workspace-renamed", { detail: { id: org.id, name } }));
      toast.success("Workspace renamed.");
    } catch (error) {
      console.error("Error renaming workspace:", error);
      setNameError(error?.response?.data?.error || "Couldn't rename the workspace. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const members = org?.members || [];
  const admins = members
    .filter((m) => m.role === "admin")
    .sort((a, b) => new Date(a.joined_at) - new Date(b.joined_at));
  const myEmail = (localStorage.getItem("email") || "").toLowerCase();

  const memberSummary = [
    `${members.length} ${members.length === 1 ? "member" : "members"}`,
    inviteCount ? `${inviteCount} pending ${inviteCount === 1 ? "invitation" : "invitations"}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="w-full font-outfit">
      <PageHeader
        title="General"
        description="Basic details for this workspace. Changes apply to everyone in it."
      />

      {orgRole !== null && !isAdmin && (
        <ViewOnlyBanner icon={PiLockSimple}>Only admins can rename this workspace.</ViewOnlyBanner>
      )}

      {loadError && (
        <div className="max-w-[760px] mb-4 flex items-center gap-3 px-4 py-3 rounded-[10px] border border-[rgba(226,25,25,0.2)] bg-[rgba(226,25,25,0.03)]">
          <span className="flex-1 text-[14px] text-[#3F3F46]">We couldn't load this workspace's details.</span>
          <SecondaryButton onClick={load}>Try again</SecondaryButton>
        </div>
      )}

      <div className="max-w-[760px]">
        <SettingRow
          label="Workspace name"
          help="Shown in the workspace switcher and to everyone you invite."
        >
          <div className="flex flex-col gap-2.5 max-w-[420px]">
            <div className="flex items-center gap-2.5">
              <span className="w-10 h-10 shrink-0 rounded-[8px] bg-[#A04A97] text-[#FAFAFA] text-[15px] font-semibold flex items-center justify-center">
                {initialsOf(isAdmin ? draftName || savedName : savedName)}
              </span>
              {isAdmin && org ? (
                <TextInput
                  value={draftName}
                  maxLength={NAME_MAX}
                  readOnly={saving}
                  onChange={(e) => {
                    setDraftName(e.target.value);
                    setNameError("");
                  }}
                  onKeyDown={(e) => e.key === "Enter" && dirty && !saving && handleSave()}
                  className="flex-1 min-w-0"
                />
              ) : (
                <LockedField className="flex-1">{savedName}</LockedField>
              )}
            </div>
            {isAdmin && org && nameError && (
              <span role="alert" className="text-[12px] text-[#E21919]">
                {nameError}
              </span>
            )}
            {isAdmin && org && !nameError && (
              <span className="text-[12px] text-[#A1A1AA]">
                {draftName.length} / {NAME_MAX} characters · initials update automatically
              </span>
            )}
          </div>
        </SettingRow>

        {admins.length > 0 && (
          <SettingRow label="Admins" help="Manage members, settings and billing.">
            <div className="flex flex-col gap-3">
              {admins.map((admin) => {
                const name = admin.full_name || admin.username;
                const isMe = (admin.email || "").toLowerCase() === myEmail;
                return (
                  <div key={admin.id} className="flex items-center gap-2.5">
                    <span className="w-8 h-8 shrink-0 rounded-full bg-[#C95EBE] text-white text-[13px] font-bold flex items-center justify-center">
                      {(name || "U").charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-[14px] font-semibold text-[#18101C]">
                        <span className="truncate">{name}</span>
                        {isMe && (
                          <span className="text-[12px] font-medium text-[#8B55DD] bg-[rgba(139,85,221,0.1)] px-1.5 py-0.5 rounded-[4px]">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-[13px] text-[#71717A] truncate">{admin.email}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </SettingRow>
        )}

        <SettingRow label="Members" help="People with access to this workspace.">
          <div className="flex items-center gap-3 flex-wrap">
            {org && <span className="text-[14px] text-[#3F3F46]">{memberSummary}</span>}
            <button
              type="button"
              onClick={() => {
                if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
                navigate("/account/team");
              }}
              className="inline-flex items-center gap-1 text-[14px] font-medium text-[#8B55DD] hover:text-[#7a3ec8]"
            >
              {isAdmin ? "Manage" : "View"}
              <PiArrowRight />
            </button>
          </div>
        </SettingRow>
      </div>

      <SaveBar
        show={dirty}
        saving={saving}
        onDiscard={() => {
          setDraftName(org?.name || "");
          setNameError("");
        }}
        onSave={handleSave}
      />
    </div>
  );
};

export default GeneralSettings;
